import { Directive, Input, OnInit, TemplateRef, ViewContainerRef, OnDestroy } from '@angular/core';
import { WorkspaceRole } from '@workspaces/models/users';
import { WorkspaceState } from '@workspaces/models/workspaces';
import { WorkspaceAuthService } from './workspace-auth.service';
import { Subscription } from 'rxjs';

export interface RequiredRoleCondition {
  role: WorkspaceRole;
  status?: WorkspaceState | WorkspaceState[];
}

export type RequiredRoleInput = WorkspaceRole | RequiredRoleCondition;

@Directive({
  selector: '[appRequiredRoles]',
  standalone: true
})
export class RequiredRoleDirective implements OnInit, OnDestroy {
  @Input('appRequiredRoles') requiredRoles: RequiredRoleInput[] = ['admin'];
  private readonly sub = new Subscription();

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainerRef: ViewContainerRef,
    private workspaceAuthService: WorkspaceAuthService
  ) {}
  private hasView = false; // Track render status
  ngOnInit(): void {
    this.sub.add(
      this.workspaceAuthService.getUserRoleWithWorkspaceStatus$().subscribe(info => {
        const authorized = this.checkAuthorization(info);

        if (authorized && !this.hasView) {
          // If authorized and view not yet rendered, render the element
          this.viewContainerRef.createEmbeddedView(this.templateRef);
          this.hasView = true;
        } else if (!authorized && this.hasView) {
          // If not authorized and view is rendered, remove the element
          this.viewContainerRef.clear();
          this.hasView = false;
        }
      })
    );
  }

  private checkAuthorization(info: { role?: WorkspaceRole; status?: WorkspaceState } | undefined): boolean {
    if (!info || !info.role) {
      return false;
    }

    return this.requiredRoles.some(required => {
      if (typeof required === 'string') {
        return info.role === required;
      }

      if (info.role !== required.role) {
        return false;
      }

      if (!required.status) {
        return true;
      }

      const allowedStatuses = Array.isArray(required.status) ? required.status : [required.status];
      return allowedStatuses.includes(info.status as WorkspaceState);
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
